import { Link } from "react-router-dom";
import { fmtDateTime, variantLabel } from "@/lib/format";
import { referenceKey, type ReferenceInfo } from "@/lib/references";
import { movementLabel } from "@/lib/inventory";
import type { BalanceRow, LedgerRow } from "../types";

// Read-only "Inventory" feature: a one-glance summary, stock by location, and
// recent ledger movements. No state, no callbacks, no fetching — it renders
// data resolved by the page.

interface Props {
  balances: BalanceRow[];
  ledger: LedgerRow[];
  references: Map<string, ReferenceInfo>;
}

const actionLink =
  "px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-md whitespace-nowrap flex items-center gap-1.5 transition-colors";

export default function InventoryTab({ balances, ledger, references }: Props) {
  const totals = balances.reduce(
    (acc, b) => ({
      onHand: acc.onHand + Number(b.on_hand ?? 0),
      allocated: acc.allocated + Number(b.allocated ?? 0),
      available: acc.available + Number(b.available ?? 0),
      incoming: acc.incoming + Number(b.incoming ?? 0),
    }),
    { onHand: 0, allocated: 0, available: 0, incoming: 0 },
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-slate-500">
          Answer the questions that matter: how much you have, where it is, and what's on the way.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/inventory" className={actionLink}>
            <i className="ri-add-line" /> Adjust stock
          </Link>
          <Link to="/transfers" className={actionLink}>
            <i className="ri-arrow-left-right-line" /> Transfer
          </Link>
          <Link to="/stocktake" className={actionLink}>
            <i className="ri-clipboard-line" /> Stocktake
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">On hand</span>
          <p className={`text-2xl font-bold mt-1 ${totals.onHand <= 0 ? "text-red-600" : "text-slate-900"}`}>{totals.onHand}</p>
          <p className="text-[11px] text-slate-400 mt-1">Physical stock across locations</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Available</span>
          <p className="text-2xl font-bold mt-1 text-emerald-700">{totals.available}</p>
          <p className="text-[11px] text-slate-400 mt-1">What you can still sell</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Allocated</span>
          <p className="text-2xl font-bold mt-1 text-slate-900">{totals.allocated}</p>
          <p className="text-[11px] text-slate-400 mt-1">Reserved for open orders</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Incoming</span>
          <p className="text-2xl font-bold mt-1 text-slate-900">{totals.incoming}</p>
          <p className="text-[11px] text-slate-400 mt-1">Due on purchase orders</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-800">Stock by Location</h3>
        </div>
        {balances.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-slate-400">No stock records</div>
        ) : (
          <div className="divide-y divide-slate-50">
            {balances.map((b) => (
              <div key={b.id} className="px-4 py-3 flex items-center justify-between flex-wrap gap-2">
                <Link
                  to={`/locations/${b.location_id}`}
                  className="text-sm font-medium text-slate-800 hover:text-emerald-700 hover:underline"
                >
                  {b.locations?.name ?? "—"}
                  {b.variants && (variantLabel(b.variants.option_values) || b.variants.sku) && (
                    <span className="text-slate-500 font-normal ml-1">· {variantLabel(b.variants.option_values) || b.variants.sku}</span>
                  )}
                </Link>
                <div className="flex items-center gap-5 text-sm">
                  <span className="text-slate-500">On hand <span className={`font-semibold ${b.on_hand <= 0 ? "text-red-600" : "text-slate-800"}`}>{b.on_hand}</span></span>
                  <span className="text-slate-500">Available <span className="font-semibold text-emerald-700">{b.available}</span></span>
                  <span className="text-slate-500">Allocated <span className="font-semibold text-slate-800">{b.allocated}</span></span>
                  <span className="text-slate-500">Incoming <span className="font-semibold text-slate-800">{b.incoming}</span></span>
                  <span className="text-slate-500">Transferring <span className="font-semibold text-slate-600">{b.transferring}</span></span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">Recent Movements</h3>
          <span className="text-xs text-slate-400">Latest {ledger.length} entries</span>
        </div>
        {ledger.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-slate-400">No movements</div>
        ) : (
          <div className="divide-y divide-slate-50">
            {ledger.map((m) => {
              const refKey = referenceKey(m.reference_type, m.reference_id);
              const ref = refKey ? references.get(refKey) : undefined;
              return (
                <div key={m.id} className="px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-600">{movementLabel(m.entry_type, m.reference_type)}</span>
                    <span className={`text-sm font-semibold ${m.qty_change >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                      {m.qty_change >= 0 ? "+" : ""}{m.qty_change}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {m.locations?.name ?? "—"} · {fmtDateTime(m.created_at)}
                    {ref && (
                      <>
                        {" · "}
                        {ref.route ? (
                          <Link to={ref.route} className="text-emerald-600 hover:text-emerald-700 hover:underline font-medium">
                            {ref.label}
                          </Link>
                        ) : (
                          <span className="text-slate-500">{ref.label}</span>
                        )}
                      </>
                    )}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}